import qalicoLogo from '@/assets/qalico.svg';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { useDeleteSourceService } from '@/services/sources';
import {
  KnowledgeGroup,
  REGION_DISPLAY_NAMES,
  Source,
  SupportedRegion,
} from '@ditto/shared';
import { ColumnDef } from '@tanstack/react-table';
import { Eye, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { DeleteUserFileModal } from './DeleteUserFileModal';

const extractInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('');
};

const formatRegions = (regions?: SupportedRegion[]): ReactNode => {
  if (!regions || regions.length === 0) {
    return '—';
  }
  return (
    <FlexRow gap="2">
      {regions.map(region => (
        <Badge variant="secondary">
          <FlexRow gap="1">
            <span>{REGION_DISPLAY_NAMES[region]}</span>
          </FlexRow>
        </Badge>
      ))}
    </FlexRow>
  );
};

export const useSourcesTableColumns = (
  knowledgeGroups: KnowledgeGroup[]
): ColumnDef<Source>[] => {
  const { deleteSource, isPending } = useDeleteSourceService();
  const groupsMap = useMemo(() => {
    const map = new Map<string, KnowledgeGroup>();
    knowledgeGroups.forEach(group => {
      map.set(group.id, group);
    });
    return map;
  }, [knowledgeGroups]);

  return useMemo(
    () => [
      {
        accessorKey: 'fileName',
        header: () => (
          <Text variant="body-sm" color="primary" className="font-weight-500">
            Document
          </Text>
        ),

        cell: ({ row }) => {
          return (
            <Ellipsis asChild lineClamp={1} hasTooltip>
              <Text variant="body-sm" color="primary">
                {row.original.fileName}
              </Text>
            </Ellipsis>
          );
        },
        meta: {
          headerClassName: 'w-[450px] min-w-[450px] max-w-[450px]',
          cellClassName: 'w-[450px] min-w-[450px] max-w-[450px]',
        },
      },
      {
        accessorKey: 'knowledgeGroup',
        header: () => (
          <Text variant="body-sm" color="primary" className="font-weight-500">
            Knowledge group
          </Text>
        ),
        cell: ({ row }) => {
          const groupId = row.original.groupId;
          if (!groupId) {
            return <div className="text-neutral-600">—</div>;
          }
          const group = groupsMap.get(groupId);
          return (
            <Badge
              color={group?.color}
              variant="outline"
              className="w-fit max-w-full h-6 rounded-lg"
            >
              <Ellipsis hasTooltip>{group?.name || '—'}</Ellipsis>
            </Badge>
          );
        },
        meta: {
          headerClassName: 'w-[170px] min-w-[170px] max-w-[170px]',
          cellClassName: 'w-[170px] min-w-[170px] max-w-[170px]',
        },
      },
      {
        accessorKey: 'regions',
        header: () => (
          <Text variant="body-sm" color="primary" className="font-weight-500">
            Regulatory region
          </Text>
        ),
        cell: ({ row }) => {
          return <div>{formatRegions(row.original.regions)}</div>;
        },
        meta: {
          headerClassName: 'w-[250px] min-w-[250px] max-w-[250px]',
          cellClassName: 'w-[250px] min-w-[250px] max-w-[250px]',
        },
      },
      {
        accessorKey: 'source',
        header: () => (
          <Text variant="body-sm" color="primary" className="font-weight-500">
            Source
          </Text>
        ),
        cell: ({ row }) => {
          if (row.original.sourceType === 'SharedKnowledgeFile') {
            return (
              <FlexRow gap="2" alignItems="center">
                <Avatar className="bg-neutral-100">
                  <AvatarImage src={qalicoLogo} />
                </Avatar>
                <Text variant="body-sm" color="primary">
                  Qalico
                </Text>
              </FlexRow>
            );
          }
          if (
            row.original.sourceType === 'UserFile' &&
            row.original.uploadedBy?.name
          ) {
            return (
              <FlexRow gap="2" alignItems="center">
                <Avatar className="bg-neutral-100">
                  <AvatarFallback>
                    {extractInitials(row.original.uploadedBy.name)}
                  </AvatarFallback>
                </Avatar>
              </FlexRow>
            );
          }
          return <div>—</div>;
        },
        meta: {
          headerClassName: 'w-[200px] min-w-[200px] max-w-[200px]',
          cellClassName: 'w-[200px] min-w-[200px] max-w-[200px]',
        },
      },
      {
        accessorKey: 'createdAt',
        header: () => (
          <Text variant="body-sm" color="primary" className="font-weight-500">
            Date added
          </Text>
        ),
        cell: ({ row }) => {
          const date = new Date(row.original.createdAt);
          return (
            <div>
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </div>
          );
        },
        meta: {
          headerClassName: 'w-[150px] min-w-[150px] max-w-[150px]',
          cellClassName: 'w-[150px] min-w-[150px] max-w-[150px]',
        },
      },
      {
        accessorKey: 'actions',
        header: () => (
          <Text variant="body-sm" color="primary" className="font-weight-500">
            Actions
          </Text>
        ),
        cell: ({ row }) => {
          const [isOpen, setIsOpen] = useState(false);
          const handlePreviewClick = () => {
            window.open(row.original.signedUrl, '_blank');
          };
          const handleDeleteClick = () => {
            setIsOpen(true);
          };

          const handleDeleteConfirm = () => {
            deleteSource(row.original.id);
            setIsOpen(false);
          };
          return (
            <FlexRow gap="2">
              <Button variant="ghost" size="icon" onClick={handlePreviewClick}>
                <Eye />
              </Button>
              {row.original.sourceType === 'UserFile' && (
                <Button variant="ghost" size="icon" onClick={handleDeleteClick}>
                  <Trash2 />
                </Button>
              )}
              <DeleteUserFileModal
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                onConfirm={handleDeleteConfirm}
                isLoading={isPending}
              />
            </FlexRow>
          );
        },
        meta: {
          headerClassName: 'w-[100px] min-w-[100px] max-w-[100px]',
          cellClassName: 'w-[100px] min-w-[100px] max-w-[100px]',
        },
      },
    ],
    [groupsMap]
  );
};
